import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TeacherMonthlyReport } from '@/types/salary';
import { useGetFsTeacherSlipUrl } from '@/hooks/useGetFsTeacherSlipUrl';
import { useTeacherGenerateSlip } from '@/hooks/useTeacherGenerateSlip';
import { useSendSlipEmail } from '@/hooks/useSendSlipEmail';
import { useAuth } from '@/context/auth/useAuth';
import { Download, FileText, CheckCircle, Clock, RefreshCw, Mail, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { th } from 'date-fns/locale';

interface SlipDownloadSectionProps {
  monthlyReport: TeacherMonthlyReport;
  teacherCode?: string;
  isTaxPnd3?: boolean;
  onSlipGenerated?: () => void;
}

export function SlipDownloadSection({ 
  monthlyReport, 
  teacherCode,
  isTaxPnd3 = true,
  onSlipGenerated 
}: SlipDownloadSectionProps) {
  const { user } = useAuth();
  const { downloadFile, isLoading: isDownloading } = useGetFsTeacherSlipUrl();
  const { generateSlip, isGenerating } = useTeacherGenerateSlip();
  const { sendSlipEmail, isSending } = useSendSlipEmail();
  const [emailInput, setEmailInput] = useState('');

  useEffect(() => {
    if (user?.email) {
      setEmailInput(user.email);
    }
  }, [user?.email]);

  // Calculate last day of the month for paid date
  const lastDayOfMonth = new Date(monthlyReport.year, monthlyReport.month, 0);
  const paidDate = format(lastDayOfMonth, 'dd-MM-yyyy');

  const hasSlip = !!monthlyReport.slip_url;
  const hasTavi50 = !!monthlyReport.tavi_50_url;
  const isConfirmed = monthlyReport.confirmation_status === 'ACCEPTED';

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: th });
    } catch {
      return null;
    }
  };

  const handleDownloadSlip = async () => {
    if (!monthlyReport.id) return;
    await downloadFile(monthlyReport.id, 'slip');
  };

  const handleDownloadTavi50 = async () => {
    if (!monthlyReport.id) return;
    await downloadFile(monthlyReport.id, 'tavi50');
  };

  const handleGenerateSlip = async () => {
    if (!monthlyReport.id || !teacherCode) return;
    
    const result = await generateSlip(
      monthlyReport,
      { teacher_code: teacherCode, isTaxPnd3 },
      paidDate
    );
    
    if (result.success && onSlipGenerated) {
      onSlipGenerated();
    }
  };

  const handleSendEmail = async () => {
    if (!monthlyReport.id) return;
    await sendSlipEmail(monthlyReport.id, emailInput || undefined);
    if (onSlipGenerated) {
      onSlipGenerated();
    }
  };

  // Show message if not confirmed
  if (!isConfirmed) {
    return (
      <Card className="mt-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            สลิปเงินเดือน
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-muted-foreground">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">กรุณายืนยันรายงานเงินเดือนก่อนดาวน์โหลดสลิป</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4" />
          สลิปเงินเดือน
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Generate Section - show if teacher can generate */}
        {teacherCode && (
          <div className="flex items-center justify-between pb-4 border-b">
            <div className="text-sm text-muted-foreground">
              วันที่จ่ายเงินเดือน: {format(lastDayOfMonth, 'dd MMMM yyyy', { locale: th })}
            </div>
            <Button
              onClick={handleGenerateSlip}
              disabled={isGenerating}
              size="sm"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
              {hasSlip ? 'สร้างสลิปใหม่' : 'สร้างสลิป'}
            </Button>
          </div>
        )}

        {/* Slip Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {hasSlip ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <Clock className="h-4 w-4 text-muted-foreground" />
            )}
            <span className={hasSlip ? 'text-foreground' : 'text-muted-foreground'}>
              สลิปเงินเดือน
            </span>
            {hasSlip && monthlyReport.generated_slip_at && (
              <span className="text-xs text-muted-foreground">
                (สร้างเมื่อ {formatDate(monthlyReport.generated_slip_at)})
              </span>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleDownloadSlip}
            disabled={!hasSlip || isDownloading}
          >
            <Download className="h-4 w-4 mr-1" />
            ดาวน์โหลด
          </Button>
        </div>

        {/* Email Input and Send */}
        {hasSlip && (
          <div className="flex items-center gap-2 pt-2 border-t">
            <Input
              type="email"
              placeholder="อีเมลสำหรับส่งสลิป"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              className="flex-1"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={handleSendEmail}
              disabled={!hasSlip || isSending || !emailInput}
            >
              <Mail className={`h-4 w-4 mr-1 ${isSending ? 'animate-pulse' : ''}`} />
              ส่งอีเมล
            </Button>
          </div>
        )}

        {/* Tavi 50 Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {hasTavi50 ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <Clock className="h-4 w-4 text-muted-foreground" />
            )}
            <span className={hasTavi50 ? 'text-foreground' : 'text-muted-foreground'}>
              หนังสือรับรองหักภาษี ณ ที่จ่าย (ทวิ 50)
            </span>
            {hasTavi50 && monthlyReport.generated_tavi_50_at && (
              <span className="text-xs text-muted-foreground">
                (สร้างเมื่อ {formatDate(monthlyReport.generated_tavi_50_at)})
              </span>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleDownloadTavi50}
            disabled={!hasTavi50 || isDownloading}
          >
            <Download className="h-4 w-4 mr-1" />
            ดาวน์โหลด
          </Button>
        </div>

        {/* Email sent info */}
        {monthlyReport.sent_slip_at && monthlyReport.sent_slip_to_email && (
          <div className="text-xs text-muted-foreground pt-2 border-t">
            ส่งอีเมลไปยัง {monthlyReport.sent_slip_to_email} เมื่อ {formatDate(monthlyReport.sent_slip_at)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
